import { parseApiDate } from './tracking-format.util';
import type {
  RagAudit, RagHit, RagLookup, RagSearch, RagServed, RagSlideCandidate, ReportSlide,
} from './tracking-demo.models';

export interface RagHitRow {
  score: number | null;
  scorePct: number;
  title: string;
  subtitle: string;
  /** Preview, whitespace-collapsed and truncated. */
  snippet: string;
  /** The whole chunk, exactly as stored. */
  fullText: string;
  /** The rest of the payload for the expanded view (text excluded — shown on its own). */
  fields: Array<{ key: string; value: string }>;
  /** How strong the similarity is, in words — a bare 0.741 tells a reader nothing. */
  matchLabel: string;
  duplicate: boolean;
  /** This chunk was part of the bundle handed to the model as its background notes. */
  sentToAi: boolean;
  /** Its 1-based number in that bundle — what an audit verdict cites. */
  sentIndex: number | null;
  pointId: string;
  /** What makes this the SAME passage as another hit — source file + chunk, never the
   *  document id (two uploads of one file differ by exactly that). Not for display. */
  identity: string;
}

export type RagSearchRole = 'primary' | 'fallback' | 'slides';

export interface RagSearchRow {
  index: number;
  /** What this search DID and what came of it, as a sentence: "Searched the knowledge
   *  base" / "found nothing". Leads the row so the numbers are never the first thing read. */
  action: string;
  outcome: string;
  /** What this search was for, in the retrieval chain. */
  role: RagSearchRole;
  roleNote: string;
  source: string;
  collection: string;
  /** The content category the model narrowed this search to (product | industry |
   *  competitor), or null when it searched everything. */
  category: string | null;
  filterText: string;
  limit: number | null;
  hits: RagHitRow[];
  hitCount: number;
  ms: number | null;
  status: string;
  failed: boolean;
  empty: boolean;
  /** This search returned nothing AND a later one in the same lookup did. */
  fellBack: boolean;
  /** Redundant copies within this top-K (a duplicated chunk counts once, not twice). */
  duplicates: number;
  /** The exchange verbatim, for the raw view. */
  raw: string;
}

/** The AI turn this lookup produced, paired from the transcript. */
export interface RagAnswerRow {
  text: string;
  time: string;
  /** True when matched by wall clock rather than by a recorded turn index. */
  inferred: boolean;
}

/** The slide decision, in terms an admin can act on. */
export interface RagSlideRow {
  ruleText: string;
  winnerTitle: string | null;
  winnerKey: string | null;
  candidates: RagSlideCandidate[];
  /** Whether the picked page is in the demo's slide history. */
  shown: 'shown' | 'not-shown' | 'unknown';
}

/** One chunk exactly as an audit sees it: numbered, whole, and named by its source. */
export interface RagSentChunk {
  n: number;
  title: string;
  subtitle: string;
  text: string;
}

/**
 * The chunk a 1-based citation `n` points at, or undefined when the citation is out of
 * range — the auditor is a model, so its citations are not guaranteed to exist.
 */
export function sentChunkAt(chunks: readonly RagSentChunk[], n: number): RagSentChunk | undefined {
  return Number.isInteger(n) && n >= 1 ? chunks[n - 1] : undefined;
}

export interface RagLookupRow {
  time: string;
  /** The whole lookup in one sentence, for a reader who reads nothing else. */
  headline: string;
  ts: string | null;
  query: string;
  /** What the tool handed back to the model. Null for calls logged before it was captured
   *  — which is not the same as having served nothing. */
  served: RagServed | null;
  answer: RagAnswerRow | null;
  slide: RagSlideRow | null;
  /** The chunks an audit judges, numbered as the verdict cites them. */
  sentChunks: RagSentChunk[];
  audit: RagAudit | null;
  consulted: boolean;
  searches: RagSearchRow[];
  searchCount: number;
  hitCount: number;
  ms: number;
  duplicates: number;
  /** Chunks that actually reached the model, and how many were different text. */
  chunksSent: number;
  distinctSent: number;
}

export interface RagSummary {
  lookups: number;
  searches: number;
  hits: number;
  ms: number;
  duplicates: number;
  emptySearches: number;
  notConsulted: number;
}

export interface RagLogView {
  summary: RagSummary;
  rows: RagLookupRow[];
}

/** Values above this many alternatives are counted rather than listed. */
const MAX_LISTED_VALUES = 3;

/** The deck index stores "no particular industry" as this sentinel (Qdrant cannot filter on
 *  a missing key), so spelling it out as `__any__` would read as a bug rather than a value. */
const ANY_SENTINEL = '__any__';

function valueText(v: any): string {
  return String(v) === ANY_SENTINEL ? 'any' : String(v);
}

/** One `{key, match}` leaf. Returns null for anything this doesn't recognise, which
 *  bubbles up as "custom filter" — describing a filter wrongly is worse than not at all. */
function clauseText(clause: any, negated: boolean): string | null {
  const key = clause?.['key'];
  const match = clause?.['match'];
  if (typeof key !== 'string' || !match || typeof match !== 'object') return null;
  const is = negated ? 'is not' : 'is';
  if ('value' in match) return `${key} ${is} ${valueText(match['value'])}`;
  if (Array.isArray(match['any'])) {
    const vals = match['any'];
    if (!vals.length) return null;
    if (vals.length > MAX_LISTED_VALUES) {
      return `${key} ${negated ? 'is none of' : 'is any of'} ${vals.length} values`;
    }
    return `${key} ${is} ${vals.map(valueText).join(' or ')}`;
  }
  return null;
}

function groupText(node: any, negated: boolean): string | null {
  if (!node || typeof node !== 'object') return null;
  const parts: string[] = [];
  for (const [op, join, flip] of [['must', ' and ', false], ['should', ' or ', false],
                                  ['must_not', ' and ', true]] as const) {
    const clauses = node[op];
    if (!Array.isArray(clauses) || !clauses.length) continue;
    const texts: string[] = [];
    for (const c of clauses) {
      const t = clauseText(c, flip ? !negated : negated) ?? groupText(c, flip ? !negated : negated);
      if (t === null) return null;   // one unreadable clause invalidates the whole description
      texts.push(t);
    }
    parts.push(texts.join(join));
  }
  return parts.length ? parts.join(' and ') : null;
}

/**
 * A Qdrant filter as a sentence an admin can read — `layer is product`,
 * `industry is education or any`, `doc_key is any of 4 values`.
 *
 * Deliberately conservative: anything it cannot describe faithfully becomes "custom filter"
 * and the reader falls through to the raw JSON, which is always one click away.
 */
export function humanFilter(filter: any): string {
  if (!filter || typeof filter !== 'object' || !Object.keys(filter).length) return 'no filter';
  return groupText(filter, false) ?? 'custom filter';
}

/** Local wall-clock HH:MM:SS, matching the report's other timestamps. */
function timeOf(iso?: string | null): string {
  const d = parseApiDate(iso);
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
}

/** Qdrant reports `time` in SECONDS; the deck-index searches run well under a millisecond,
 *  so this stays fractional and the template decides how to round it. */
function msOf(response: any): number | null {
  const t = response?.['time'];
  return typeof t === 'number' && isFinite(t) ? t * 1000 : null;
}

function hitsOf(search: RagSearch | null | undefined): RagHit[] {
  const result = search?.response?.['result'];
  return Array.isArray(result) ? result.filter((h) => h && typeof h === 'object') : [];
}

/** How much of a chunk is worth showing before it stops being a preview. */
const SNIPPET_MAX = 220;

function snippetOf(text: any): string {
  const t = String(text ?? '').replace(/\s+/g, ' ').trim();
  return t.length > SNIPPET_MAX ? `${t.slice(0, SNIPPET_MAX)}…` : t;
}

/** The deck index lives in its own collection alongside the KB, and its points carry a
 *  completely different payload — deck/title/page_key rather than document_id/chunk_index. */
function isSlideCollection(collection: string): boolean {
  return collection.endsWith('_slides');
}

/**
 * What makes two hits THE SAME chunk, for duplicate detection.
 *
 * A KB chunk is identified by its source file + chunk index, deliberately NOT by
 * document_id: two uploads of the same PDF get different document_ids, and that is exactly
 * the duplication worth surfacing. Deck pages carry a content_hash that says it outright.
 */
function identityOf(hit: RagHit, slides: boolean): string {
  const p = hit?.payload || {};
  if (slides) {
    return String(p['content_hash'] ?? `${p['deck'] ?? ''}|${p['title'] ?? ''}`);
  }
  const name = p['original_name'] ?? p['title'];
  const chunk = p['chunk_index'];
  if (name != null && chunk != null) return `${name}|${chunk}`;
  return snippetOf(p['text']);
}

/** Cosine similarity in words. The bands are read off what real calls return: the deck
 *  index floors at 0.4 and useful knowledge hits sit around 0.6-0.75, so 0.74 is a strong
 *  match here even though it would be a poor one on an absolute scale. */
function matchLabelOf(score: number | null): string {
  if (score == null) return 'No score';
  if (score >= 0.70) return 'Strong match';
  if (score >= 0.60) return 'Good match';
  if (score >= 0.50) return 'Fair match';
  return 'Weak match';
}

function buildHitRow(hit: RagHit, slides: boolean, duplicate: boolean, identity: string): RagHitRow {
  const p = hit?.payload || {};
  const score = typeof hit?.score === 'number' && isFinite(hit.score) ? hit.score : null;
  let title: string;
  let subtitle: string;
  if (slides) {
    title = String(p['title'] || p['page_key'] || '(untitled slide)');
    subtitle = String(p['deck'] || '');
  } else {
    title = String(p['original_name'] || p['title'] || '(untitled document)');
    const bits: string[] = [];
    if (p['chunk_index'] != null) bits.push(`chunk ${p['chunk_index']}`);
    if (p['document_id'] != null) bits.push(`doc ${p['document_id']}`);
    subtitle = bits.join(' · ');
  }
  return {
    score,
    scorePct: score == null ? 0 : Math.round(score * 100),
    matchLabel: matchLabelOf(score),
    title,
    subtitle,
    snippet: snippetOf(p['text']),
    fullText: String(p['text'] ?? ''),
    fields: Object.entries(p)
      .filter(([k, v]) => k !== 'text' && v !== null && v !== undefined && v !== '')
      .map(([key, v]) => ({ key, value: Array.isArray(v) ? v.join(', ') : String(v) })),
    duplicate,
    sentToAi: false,   // decided per lookup — needs the sibling searches
    sentIndex: null,
    pointId: String(hit?.id ?? ''),
    identity,
  };
}

/** Every hit belonging to a group of two or more is flagged, so the pair is visible as a
 *  pair; the COUNT only charges for the redundant copies (a duplicated chunk costs one
 *  wasted slot in the top-K, not two). */
function buildHitRows(hits: RagHit[], slides: boolean): { rows: RagHitRow[]; duplicates: number } {
  const seen = new Map<string, number>();
  for (const h of hits) {
    const key = identityOf(h, slides);
    seen.set(key, (seen.get(key) || 0) + 1);
  }
  let duplicates = 0;
  for (const n of seen.values()) if (n > 1) duplicates += n - 1;
  return {
    rows: hits.map((h) => {
      const key = identityOf(h, slides);
      return buildHitRow(h, slides, (seen.get(key) || 0) > 1, key);
    }),
    duplicates,
  };
}

/**
 * The category the model picked for this search.
 *
 * It reaches Qdrant as a `layer` equality inside `must` (knowledge.py
 * _category_filter turns product | industry | competitor into exactly that). The
 * unfiltered fallback also constrains `layer`, but by EXCLUDING the playbook inside a
 * `should`/`must_not` — that is not a category anyone chose, so only the must-equality
 * counts here.
 */
function categoryOf(filter: any): string | null {
  const must = filter?.['must'];
  if (!Array.isArray(must)) return null;
  for (const clause of must) {
    if (clause?.['key'] !== 'layer') continue;
    const value = clause?.['match']?.['value'];
    if (typeof value === 'string' && value) return value;
  }
  return null;
}

function buildSearchRow(search: RagSearch, index: number): RagSearchRow {
  const collection = search?.collection || '';
  const slides = isSlideCollection(collection);
  const request = search?.request || {};
  const response = search?.response || {};
  const rawStatus = response['status'];
  const status = typeof rawStatus === 'string' ? rawStatus : rawStatus ? 'error' : '';
  const limit = request['limit'];
  const { rows, duplicates } = buildHitRows(hitsOf(search), slides);
  const role: RagSearchRole = slides ? 'slides' : 'primary';   // refined by buildLookupRow
  return {
    index: index + 1,
    role,
    roleNote: '',
    action: '',      // both need the sibling searches to know which search this is
    outcome: '',
    source: slides ? 'Slide picker' : 'Knowledge base',
    collection,
    category: categoryOf(request['filter']),
    filterText: humanFilter(request['filter']),
    limit: typeof limit === 'number' ? limit : null,
    hits: rows,
    hitCount: rows.length,
    ms: msOf(search?.response),
    status,
    // `_unparsed` is what the logger stores when Qdrant answered with something that
    // wasn't JSON at all (a gateway error page, typically).
    failed: !!response['_unparsed'] || (status !== '' && status !== 'ok'),
    empty: rows.length === 0,
    fellBack: false,   // needs the whole lookup — filled in by buildLookupRow
    duplicates,
    raw: JSON.stringify({ request: search?.request ?? null, response: search?.response ?? null }, null, 2),
  };
}

const ROLE_NOTES: Record<RagSearchRole, string> = {
  primary: 'The first knowledge search for this question.',
  fallback: 'Re-run unfiltered because the first search came back short — these are the chunks that answered.',
  slides: 'Picks which deck page goes on screen — not part of the answer text.',
};

/** Naming the category is the point: "the category the model chose" leaves the reader
 *  unable to judge whether it was a sensible choice for the question asked. */
const filteredPrimaryNote = (category: string | null): string =>
  category
    ? `The first knowledge search, narrowed to the “${category}” category that the model `
      + 'chose for this question.'
    : 'The first knowledge search, narrowed by a filter the model chose for this question.';

/**
 * Which search was which in the retrieval chain.
 *
 * Assigned by collection and by order among the KNOWLEDGE searches only: the deck lookup
 * runs concurrently and lands anywhere in the sequence, so counting raw positions would
 * label it the fallback (it did, in call #4148).
 */
function assignRoles(searches: RagSearchRow[]): void {
  let knowledgeSeen = 0;
  for (const s of searches) {
    if (s.role === 'slides') {
      s.roleNote = ROLE_NOTES.slides;
      s.action = 'Looked for a slide to show';
      s.outcome = outcomeOf(s, 'possible slide');
      continue;
    }
    knowledgeSeen += 1;
    s.role = knowledgeSeen === 1 ? 'primary' : 'fallback';
    s.roleNote = s.role === 'primary' && s.filterText !== 'no filter'
      ? filteredPrimaryNote(s.category)
      : ROLE_NOTES[s.role];
    s.action = s.role === 'primary'
      ? 'Searched the knowledge base'
      : 'Searched again, without the category filter';
    s.outcome = outcomeOf(s, 'result');
  }
}

/** What came back, in words. A failed search is called a failure: rendering it as "found
 *  nothing" would read as "the knowledge base has no answer", which is a different problem
 *  with a different fix. */
function outcomeOf(search: RagSearchRow, noun: string): string {
  if (search.failed) return 'the search failed';
  if (!search.hitCount) return 'found nothing';
  return `found ${search.hitCount} ${noun}${search.hitCount === 1 ? '' : 's'}`;
}

/**
 * Flag the chunks that actually became the model's background notes.
 *
 * Mirrors KnowledgeService.search(): the filtered hits, then any fallback hit not already
 * among them, truncated to the requested top-K. Deck-slide hits are never part of it —
 * that text is written to be spoken over a visual and never enters the notes bundle.
 */
function markChunksSentToAi(
  searches: RagSearchRow[],
): { sent: number; distinct: number; chunks: RagSentChunk[] } {
  const knowledge = searches.filter((s) => s.role !== 'slides');
  const topK = knowledge.find((s) => s.limit != null)?.limit ?? Infinity;
  const seen = new Set<string>();
  const identities: string[] = [];
  const chunks: RagSentChunk[] = [];
  for (const s of knowledge) {
    for (const h of s.hits) {
      if (seen.size >= topK) break;
      if (seen.has(h.pointId)) continue;
      seen.add(h.pointId);
      h.sentToAi = true;
      h.sentIndex = chunks.length + 1;
      identities.push(h.identity);
      chunks.push({ n: h.sentIndex, title: h.title, subtitle: h.subtitle, text: h.fullText });
    }
  }
  return { sent: seen.size, distinct: new Set(identities).size, chunks };
}

/**
 * Pair each lookup with the answer that came out of it: the first AI turn stamped after
 * the lookup ran.
 *
 * Inferred, and labelled as such. The transcript stamps a turn when it is COMMITTED, not
 * when the speech began — in call #4148 every lookup fires seconds before the prospect
 * turn that prompted it is even timestamped — so only the AI side is a safe anchor.
 * `turnIndex` now rides new records and will make this exact; until calls carry it, the
 * report must not present a guess as a fact.
 */
function attachAnswers(
  rows: RagLookupRow[],
  turns: Array<{ who?: string; text?: string; ts?: string }> | null,
  isAi: (turn: any) => boolean,
): void {
  const aiTurns = (Array.isArray(turns) ? turns : [])
    .filter((t) => t && isAi(t) && (t.text || '').trim())
    .map((t) => ({ turn: t, at: parseApiDate(t.ts)?.getTime() ?? null }))
    .filter((t) => t.at !== null)
    .sort((a, b) => (a.at as number) - (b.at as number));
  if (!aiTurns.length) return;
  for (const row of rows) {
    const at = parseApiDate(row.ts)?.getTime();
    if (at == null) continue;
    const next = aiTurns.find((t) => (t.at as number) > at);
    if (!next) continue;
    row.answer = {
      text: next.turn.text || '',
      time: timeOf(next.turn.ts),
      inferred: true,
    };
  }
}

/** Deck keys are compared stripped and lower-cased: the slide history records the key as
 *  it was addressed at the time, which a page rename can punctuate differently. */
const deckKeyId = (key: unknown): string =>
  String(key ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

function ruleTextOf(rule: string, floor: number | undefined, industry: string | null): string {
  const at = floor == null ? '' : ` (floor ${floor})`;
  switch (rule) {
    case 'industry_near_tie':
      return `The top page was a generic one, and this ${industry || 'industry'} page scored `
        + `within the near-tie margin — so the industry page won${at}.`;
    case 'terminal_first':
      return 'A near-tie was resolved to the Terminal deck, which is the default demo for a '
        + `call (terminal-pitch-first)${at}.`;
    case 'none_above_floor':
      return `No candidate reached the ${floor} score floor, so no deck page opened — the `
        + 'agent fell back to a generated card.';
    default:
      return `Highest-scoring candidate${at}. No near-tie rule applied.`;
  }
}

/**
 * Attach "why this page opened" to each lookup that asked the deck index for one, and
 * cross-check it against what the prospect actually saw.
 *
 * The picked page and the shown page can differ — in call #4148 the pick for "list all
 * Credee payment plans" appears nowhere in the slide history — and that gap is invisible
 * unless the two are put side by side.
 */
function attachSlideDecisions(
  rows: RagLookupRow[], lookups: RagLookup[] | null | undefined, slidesShown: ReportSlide[] | null,
): void {
  const source = (Array.isArray(lookups) ? lookups : []).filter((l) => l && typeof l === 'object');
  const shownKeys = new Set((slidesShown || []).map((s) => deckKeyId(s?.deckKey)).filter(Boolean));
  rows.forEach((row, i) => {
    const decision = source[i]?.slideDecision;
    if (!decision || typeof decision !== 'object') return;
    const winnerKey = decision.winner?.key ?? null;
    row.slide = {
      ruleText: ruleTextOf(String(decision.rule || ''), decision.floor, decision.industryKey ?? null),
      winnerTitle: decision.winner?.title || null,
      winnerKey,
      candidates: Array.isArray(decision.candidates) ? decision.candidates : [],
      shown: !slidesShown || !slidesShown.length || !winnerKey
        ? 'unknown'
        : shownKeys.has(deckKeyId(winnerKey)) ? 'shown' : 'not-shown',
    };
  });
}

/** The lookup in one sentence. Names the duplication where there is any, because "6 pieces
 *  of knowledge" and "6 pieces of which 4 are different" are very different facts. */
function headlineOf(consulted: boolean, sent: number, distinct: number, agent: string): string {
  if (!consulted) {
    return 'Answered from the standard fee sheet — the knowledge base was not searched';
  }
  if (!sent) return `Nothing from the knowledge base reached ${agent}`;
  const pieces = `${sent} ${sent === 1 ? 'piece' : 'pieces'} of knowledge`;
  return distinct < sent
    ? `${agent} was given ${pieces} — only ${distinct} are different`
    : `${agent} was given ${pieces}`;
}

function buildLookupRow(lookup: RagLookup, agentName: string = 'Alex'): RagLookupRow {
  const searches = (Array.isArray(lookup?.qdrant) ? lookup.qdrant : [])
    .filter((s) => s && typeof s === 'object')
    .map(buildSearchRow);
  // The knowledge service retries a filtered search unfiltered when it comes back short, so
  // an empty search followed by a productive one is the fallback firing, not a dead end.
  searches.forEach((s, i) => {
    s.fellBack = s.empty && searches.slice(i + 1).some((later) => later.hitCount > 0);
  });
  assignRoles(searches);
  const { sent, distinct, chunks: sentChunks } = markChunksSentToAi(searches);
  return {
    time: timeOf(lookup?.ts),
    headline: headlineOf(searches.length > 0, sent, distinct, agentName),
    ts: lookup?.ts || null,
    query: (lookup?.query || '').trim() || '(no query recorded)',
    served: (lookup?.served && typeof lookup.served === 'object') ? lookup.served : null,
    answer: null,   // needs the transcript — attached by buildRagLog
    slide: null,    // needs the slide history — attached by buildRagLog
    audit: (lookup?.audit && typeof lookup.audit === 'object') ? lookup.audit : null,
    consulted: searches.length > 0,
    searches,
    searchCount: searches.length,
    hitCount: searches.reduce((n, s) => n + s.hitCount, 0),
    ms: Math.round(searches.reduce((n, s) => n + (s.ms || 0), 0)),
    duplicates: searches.reduce((n, s) => n + s.duplicates, 0),
    chunksSent: sent,
    distinctSent: distinct,
    sentChunks,
  };
}

/**
 * The RAG tab's whole view model, built once per report load.
 *
 * Pure and total: the stored record is written by a best-effort logger against a live call,
 * so every field is treated as optional and a malformed entry degrades to a readable row
 * rather than blanking the tab.
 */
export function buildRagLog(
  lookups: RagLookup[] | null | undefined,
  turns: Array<{ who?: string; text?: string; ts?: string }> | null = null,
  isAi: (turn: any) => boolean = (t) => (t?.who || '').toLowerCase() === 'alex',
  slidesShown: ReportSlide[] | null = null,
  /** The call's agent persona (Alex / Ava / Dena) for the prose. */
  agentName: string = 'Alex',
): RagLogView {
  const rows = (Array.isArray(lookups) ? lookups : [])
    .filter((l) => l && typeof l === 'object')
    .map((l) => buildLookupRow(l, agentName));
  attachAnswers(rows, turns, isAi);
  attachSlideDecisions(rows, lookups, slidesShown);
  return {
    summary: {
      lookups: rows.length,
      searches: rows.reduce((n, r) => n + r.searchCount, 0),
      hits: rows.reduce((n, r) => n + r.hitCount, 0),
      ms: Math.round(rows.reduce((n, r) => n + r.ms, 0)),
      duplicates: rows.reduce((n, r) => n + r.duplicates, 0),
      emptySearches: rows.reduce((n, r) => n + r.searches.filter((s) => s.empty).length, 0),
      notConsulted: rows.filter((r) => !r.consulted).length,
    },
    rows,
  };
}
